import { type Colors } from '@factorialco/deprecated-design-system'
import { xor } from 'lodash'

import BarChart from 'modules/core/components/BarChart'
import type { Filter } from 'modules/core/components/DataTable/types'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { getSelectedFilterAndOptionByIndex } from 'modules/performance_analytics/components/EmployeePulseInsights/lib/filterUtils'
import type { ProcessedChartData } from 'modules/performance_analytics/components/EmployeePulseInsights/types'
import { GROUP_BY_COLORS, type GroupByType } from 'modules/performance_analytics/components/helpers'

import { ChartTooltip } from './ChartTooltip'

interface ChartClickDatum {
  index: number
  label: string
}

interface PulseChartProps {
  processedData: ProcessedChartData
  colors: Colors[]
  dataTableFilters: Record<string, unknown>
  enabledFilters: Filter<unknown>[]
  availableFilters: Filter<unknown>[]
  onAddFilter: (filter: Filter<unknown>) => void
  onUpdateFilter: (filter: Filter<unknown>) => void
  groupBy?: GroupByType
  useCustomTooltip?: boolean // New prop to control tooltip usage
}

const hasInProperty = (obj: unknown): obj is { in?: unknown[] } => {
  return Boolean(obj && typeof obj === 'object' && 'in' in obj)
}

const buildKeyColorMap = (keys: string[], colors: Colors[]): Record<string, Colors> => {
  return keys.reduce<Record<string, Colors>>((acc, key, index) => {
    acc[key] = colors[index % colors.length] ?? 'viridian400'
    return acc
  }, {})
}

export const PulseChart = ({
  processedData,
  colors,
  dataTableFilters,
  enabledFilters,
  availableFilters,
  onAddFilter,
  onUpdateFilter,
  groupBy,
  useCustomTooltip = false,
}: PulseChartProps) => {
  const { regularChartData, stackedChartData, finalKeys, maxOfGroupBy, showGroupBy } = processedData

  const chartData = showGroupBy ? stackedChartData : regularChartData
  const chartColors = showGroupBy ? GROUP_BY_COLORS : colors

  const handleChartClick = (datum: ChartClickDatum) => {
    const { pulseFilter, selectedOption, filterAlreadyEnabled } = getSelectedFilterAndOptionByIndex(
      datum.index + 1,
      enabledFilters,
      availableFilters
    )

    if (!pulseFilter || !selectedOption) {
      return
    }

    track(trackingEvents.performanceAnalytics.insightsSummaryScoreBarClicked, {
      category: datum.label,
    })

    const pulseAnswerFilter = dataTableFilters.pulseAnswer
    const currentValue = hasInProperty(pulseAnswerFilter) ? pulseAnswerFilter.in || [] : []
    // Toggle the selected value: add if not present, remove if already selected
    const nextValue = xor(currentValue, [selectedOption.value])
    const nextFilter = { ...pulseFilter, value: nextValue }

    // Prevent dropdown auto-opening when applying filter programmatically
    // Example: when user clicks chart bar instead of filter dropdown directly
    nextFilter.noAutoOpen = true

    if (filterAlreadyEnabled) {
      onUpdateFilter(nextFilter)
      return
    }

    onAddFilter(nextFilter)
  }

  // Use custom tooltip only for grouped mode, default tooltip for stacked mode
  const currentGroupMode = showGroupBy ? 'stacked' : 'grouped'
  const shouldUseCustomTooltip = useCustomTooltip && currentGroupMode === 'grouped'

  const CustomTooltip = shouldUseCustomTooltip
    ? ChartTooltip({
        chartData,
        groupBy: showGroupBy ? groupBy : undefined,
        keyColorMap: buildKeyColorMap(finalKeys, chartColors),
      })
    : undefined

  return (
    <BarChart
      margin={{
        top: 20,
        bottom: showGroupBy ? (finalKeys.length > 9 ? 140 : 120) : 100,
        left: 30,
        right: -20,
      }}
      colors={chartColors}
      colorBy={showGroupBy ? 'id' : 'indexValue'}
      maxValue={maxOfGroupBy}
      groupMode={showGroupBy ? 'stacked' : 'grouped'}
      keys={finalKeys}
      indexBy={'bucket'}
      axisBottom={{
        format: (label: string) => label,
        renderTick: ({ x, y, value }) => (
          <g transform={`translate(${x},${y + 18})`}>
            <text
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontFamily: 'Inter',
                fontSize: 12,
              }}
            >
              {value}
            </text>
          </g>
        ),
      }}
      tooltip={CustomTooltip}
      useCustomTooltip={shouldUseCustomTooltip}
      enabledGridY
      data={chartData}
      showLegend={showGroupBy}
      onClick={handleChartClick}
      pointer
    />
  )
}
